/**
 * Maps a raw PDE JSON document into ViewerSection[] for the DataViewer component.
 * Each PDE section becomes a collapsible section with typed columns.
 */
import { ViewerSection, ViewerColumn } from "@/components/common/data-viewer/DataViewerModels";
import { PdeDocument, PDE_SECTION_KEYS } from "@/data/models/PdeDocument";
import { i18n } from "@/plugins/vuetify";

/** Human-readable section names and column definitions per PDE section */
interface PdeSectionConfig {
  nameKey: string;
  columns: ViewerColumn[];
  /** Whether this section starts expanded */
  expandedByDefault?: boolean;
  /**
   * Columns a later PDE schema dropped. They stay for older files that still carry the field,
   * and are left out when no row of the file has it, so a new file shows no permanently empty column.
   */
  retiredColumnKeys?: string[];
}

function t(key: string): string {
  return i18n.t(key) as string;
}

/** Column definition helper */
function col(key: string, labelKey: string, options?: Partial<ViewerColumn>): ViewerColumn {
  return {
    key,
    label: t(labelKey),
    searchable: true,
    visible: true,
    ...options,
  };
}

/** Get section configuration for each PDE section */
function getSectionConfigs(): Record<keyof PdeDocument, PdeSectionConfig> {
  return {
    "0_projectInfo": {
      nameKey: "pdeViewer.sections.projectInfo",
      expandedByDefault: true,
      columns: [
        col("projectIdIres", "pdeViewer.fields.projectIdIres"),
        col("projectIdErp", "pdeViewer.fields.projectIdErp"),
        col("projectTypeId", "pdeViewer.fields.projectTypeId"),
        col("creationTimestamp", "pdeViewer.fields.creationTimestamp"),
        col("pdeSchemaVersion", "pdeViewer.fields.pdeSchemaVersion"),
        col("infoConfigVersion", "pdeViewer.fields.infoConfigVersion"),
        col("erpBoQVersion", "pdeViewer.fields.erpBoQVersion"),
        col("senderId", "pdeViewer.fields.senderId"),
        col("projectStatus", "pdeViewer.fields.projectStatus"),
      ],
    },
    "1_apartments": {
      nameKey: "pdeViewer.sections.apartments",
      columns: [
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("contactPersonLastName", "pdeViewer.fields.lastName"),
        col("contactPersonFirstName", "pdeViewer.fields.firstName"),
        col("floor", "pdeViewer.fields.floor"),
      ],
    },
    "2_rooms": {
      nameKey: "pdeViewer.sections.rooms",
      columns: [
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("roomNumber", "pdeViewer.fields.roomNumber"),
        col("roomName", "pdeViewer.fields.roomName"),
        col("note", "pdeViewer.fields.note"),
        col("numberOfWalls", "pdeViewer.fields.numberOfWalls"),
        col("roomWidth", "pdeViewer.fields.roomWidth"),
        col("roomLength", "pdeViewer.fields.roomLength"),
        col("roomHeight", "pdeViewer.fields.roomHeight"),
        col("roomArea", "pdeViewer.fields.roomArea"),
      ],
    },
    "3_consolidatedWorkItems": {
      nameKey: "pdeViewer.sections.consolidatedWorkItems",
      expandedByDefault: true,
      columns: [
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("roomNumber", "pdeViewer.fields.roomNumber"),
        col("component", "pdeViewer.fields.component"),
        col("workItemId", "pdeViewer.fields.workItemId"),
        col("billingItemId", "pdeViewer.fields.billingItemId", { editable: true, clickable: true }),
        col("billingItemId2", "pdeViewer.fields.billingItemId2", { editable: true }),
        col("workItemText", "pdeViewer.fields.workItemText", { width: "200px" }),
        col("billingItemTextErp", "pdeViewer.fields.billingItemTextErp", { editable: true, width: "200px" }),
        col("quantityIres", "pdeViewer.fields.quantityIres", { editType: "number" }),
        col("quantityErp", "pdeViewer.fields.quantityErp", {
          editable: true,
          editType: "picker",
          // Business rule: quantityErp may only be empty (null), 0, or the
          // row's own quantityIres — the ERP accepts the full IRES quantity or
          // rejects it entirely, never a partial value.
          pickerOptionsForRow: (row) => {
            const options: { value: string | number | null; label: string }[] = [
              { value: null, label: t("pdeViewer.noSelection") },
              { value: 0, label: "0" },
            ];
            const quantityIres = row.quantityIres;
            if (typeof quantityIres === "number" && quantityIres !== 0) {
              options.push({ value: quantityIres, label: String(quantityIres) });
            }
            return options;
          },
        }),
        col("quantityCompleted", "pdeViewer.fields.quantityCompleted", { editType: "number" }),
        col("unit", "pdeViewer.fields.unit"),
        col("phase", "pdeViewer.fields.phase"),
        col("trade", "pdeViewer.fields.trade"),
        col("serviceProviderIres", "pdeViewer.fields.serviceProviderIres"),
        col("serviceProviderErp", "pdeViewer.fields.serviceProviderErp", {
          editable: true,
          editType: "picker",
          // ERP service-provider values (written into the ERP-consumed PDE).
          // Empty (null) shown as "Keine Auswahl".
          pickerOptions: [
            { value: null, label: t("pdeViewer.noSelection") },
            { value: "INTERNAL", label: "INTERNAL" },
            { value: "EXTERNAL", label: "EXTERNAL" },
            { value: "POLICY_HOLDER", label: "POLICY_HOLDER" },
            { value: "UNASSIGNED", label: "UNASSIGNED" },
          ],
        }),
      ],
    },
    "4_materials": {
      nameKey: "pdeViewer.sections.materials",
      columns: [
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("chapter", "pdeViewer.fields.chapter"),
        col("section", "pdeViewer.fields.section"),
        col("paragraph", "pdeViewer.fields.paragraph"),
        col("asset", "pdeViewer.fields.asset"),
        col("quantity", "pdeViewer.fields.quantity", { editType: "number" }),
        col("unit", "pdeViewer.fields.unit"),
      ],
    },
    "5_timeEstimate": {
      nameKey: "pdeViewer.sections.timeEstimate",
      columns: [
        col("technicianId", "pdeViewer.fields.technicianId"),
        col("protectionHours", "pdeViewer.fields.protectionHours", { editType: "number" }),
        col("dismantlingHours", "pdeViewer.fields.dismantlingHours", { editType: "number" }),
        col("setupDryingHours", "pdeViewer.fields.setupDryingHours", { editType: "number" }),
        col("measurementHours", "pdeViewer.fields.measurementHours", { editType: "number" }),
        col("packupDryingHours", "pdeViewer.fields.packupDryingHours", { editType: "number" }),
        col("restorationHours", "pdeViewer.fields.restorationHours", { editType: "number" }),
      ],
    },
    "6_timeTracking": {
      nameKey: "pdeViewer.sections.timeTracking",
      columns: [
        col("entryNumber", "pdeViewer.fields.entryNumber"),
        col("date", "pdeViewer.fields.date"),
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("phase", "pdeViewer.fields.phase"),
        col("userNumber", "pdeViewer.fields.userNumber"),
        col("technicianId", "pdeViewer.fields.technicianId"),
        col("startOfWork", "pdeViewer.fields.startOfWork"),
        col("endOfWork", "pdeViewer.fields.endOfWork"),
        col("leakDetectionHours", "pdeViewer.fields.leakDetectionHours", { editType: "number" }),
        col("damageAssessmentHours", "pdeViewer.fields.damageAssessmentHours", { editType: "number" }),
        col("protectionHours", "pdeViewer.fields.protectionHours", { editType: "number" }),
        col("dismantlingHours", "pdeViewer.fields.dismantlingHours", { editType: "number" }),
        col("setupDryingHours", "pdeViewer.fields.setupDryingHours", { editType: "number" }),
        col("measurementHours", "pdeViewer.fields.measurementHours", { editType: "number" }),
        col("packupDryingHours", "pdeViewer.fields.packupDryingHours", { editType: "number" }),
        col("restorationHours", "pdeViewer.fields.restorationHours", { editType: "number" }),
        col("travelTimeHours", "pdeViewer.fields.travelTimeHours", { editType: "number" }),
        col("workDone", "pdeViewer.fields.workDone"),
        col("materialUsed", "pdeViewer.fields.materialUsed"),
      ],
    },
    "8_deviceAssignments": {
      nameKey: "pdeViewer.sections.deviceAssignments",
      columns: [
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("roomNumber", "pdeViewer.fields.roomNumber"),
        col("deviceAssignmentNumber", "pdeViewer.fields.deviceAssignmentNumber"),
        col("deviceType", "pdeViewer.fields.deviceType"),
        col("deviceId", "pdeViewer.fields.deviceId"),
        col("countingDeviceId", "pdeViewer.fields.countingDeviceId"),
        col("electricityMeterType", "pdeViewer.fields.electricityMeterType"),
        col("meterStart", "pdeViewer.fields.meterStart", { editType: "number" }),
        col("meterEnd", "pdeViewer.fields.meterEnd", { editType: "number" }),
        col("consumption", "pdeViewer.fields.consumption"),
        col("fromDate", "pdeViewer.fields.fromDate"),
        col("toDate", "pdeViewer.fields.toDate"),
        col("note", "pdeViewer.fields.note"),
      ],
    },
    "10_workItems": {
      nameKey: "pdeViewer.sections.workItems",
      // IRES Project stopped writing wallNumber here on 2026-10-07: the wall name moved into `component`.
      retiredColumnKeys: ["wallNumber"],
      columns: [
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("roomNumber", "pdeViewer.fields.roomNumber"),
        col("component", "pdeViewer.fields.component"),
        col("wallNumber", "pdeViewer.fields.wallNumber"),
        col("workItemId", "pdeViewer.fields.workItemId"),
        col("billingItemId", "pdeViewer.fields.billingItemId", { clickable: true }),
        col("workItemText", "pdeViewer.fields.workItemText", { width: "200px" }),
        col("billingItemTextErp", "pdeViewer.fields.billingItemTextErp", { width: "200px" }),
        col("quantityIres", "pdeViewer.fields.quantityIres", { editType: "number" }),
        col("quantityErp", "pdeViewer.fields.quantityErp", { editType: "number" }),
        col("quantityCompleted", "pdeViewer.fields.quantityCompleted", { editType: "number" }),
        col("unit", "pdeViewer.fields.unit"),
        col("phase", "pdeViewer.fields.phase"),
        col("trade", "pdeViewer.fields.trade"),
        col("serviceProviderIres", "pdeViewer.fields.serviceProviderIres"),
        col("serviceProviderErp", "pdeViewer.fields.serviceProviderErp"),
      ],
    },
    "11_affectedComponents": {
      nameKey: "pdeViewer.sections.affectedComponents",
      columns: [
        col("apartmentNumber", "pdeViewer.fields.apartmentNumber"),
        col("roomNumber", "pdeViewer.fields.roomNumber"),
        col("component", "pdeViewer.fields.component"),
        col("wallNumber", "pdeViewer.fields.wallNumber"),
        col("componentWidth", "pdeViewer.fields.componentWidth"),
        col("componentLength", "pdeViewer.fields.componentLength"),
        col("componentHeight", "pdeViewer.fields.componentHeight"),
        col("componentArea", "pdeViewer.fields.componentArea"),
        col("damageWidth", "pdeViewer.fields.damageWidth"),
        col("damageLength", "pdeViewer.fields.damageLength"),
        col("damageHeight", "pdeViewer.fields.damageHeight"),
        col("damageArea", "pdeViewer.fields.damageArea"),
      ],
    },
  };
}

/** The section's columns, minus each retired column that no row of this file carries. */
function withoutAbsentRetiredColumns(
  config: PdeSectionConfig,
  rows: Record<string, unknown>[]
): ViewerColumn[] {
  const retired = config.retiredColumnKeys ?? [];
  return config.columns.filter(
    (column) => !retired.includes(column.key) || rows.some((row) => column.key in row)
  );
}

/**
 * Converts a raw PDE JSON object into ViewerSection[] for the DataViewer.
 * Handles unknown sections gracefully by generating columns from the first row.
 */
export function mapPdeToViewerSections(pdeData: Record<string, unknown>): ViewerSection[] {
  const configs = getSectionConfigs();
  const sections: ViewerSection[] = [];

  // Process known sections in order
  for (const sectionKey of PDE_SECTION_KEYS) {
    const rawSection = pdeData[sectionKey];
    if (!Array.isArray(rawSection) || rawSection.length === 0) continue;

    const config = configs[sectionKey];
    const rows = rawSection as Record<string, string | number | null>[];
    sections.push({
      id: sectionKey,
      name: t(config.nameKey),
      type: sectionKey.split("_")[0] ?? sectionKey,
      columns: withoutAbsentRetiredColumns(config, rows),
      rows,
      expandedByDefault: config.expandedByDefault,
    });
  }

  // Process any unknown sections (future-proofing)
  for (const key of Object.keys(pdeData)) {
    if (PDE_SECTION_KEYS.includes(key as keyof PdeDocument)) continue;
    const rawSection = pdeData[key];
    if (!Array.isArray(rawSection) || rawSection.length === 0) continue;

    const firstRow = rawSection[0] as Record<string, unknown>;
    const autoColumns: ViewerColumn[] = Object.keys(firstRow).map((fieldKey) => ({
      key: fieldKey,
      label: fieldKey,
      searchable: true,
      visible: true,
    }));

    sections.push({
      id: key,
      name: key,
      type: key.split("_")[0] ?? key,
      columns: autoColumns,
      rows: rawSection as Record<string, string | number | null>[],
    });
  }

  return sections;
}
